import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchPdfBytes, loadPdfLibrary, PDFJS_PATH } from "./loadPdf";
import "./pdf-viewer.css";

function useVisible(ref, root = null, once = true) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    if (!window.IntersectionObserver) { setVisible(true); return; }
    const observer = new IntersectionObserver((entries) => {
      const inView = entries.some((entry) => entry.isIntersecting);
      if (!once || inView) setVisible(inView);
    }, { root, rootMargin: "400px" });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, root, once]);
  return visible;
}
function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setWidth(Math.max(1, element.clientWidth));
    measure();
    if (!window.ResizeObserver) {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

// Uma página por vez é desenhada quando chega perto da área de leitura.
// O canvas acompanha o zoom e a densidade da tela, preservando a nitidez do PDF.
function PdfPage({ pdf, number, width, preview = false, scrollRoot = null }) {
  const container = useRef(null);
  const canvas = useRef(null);
  const visible = useVisible(container, scrollRoot, false);
  const [ratio, setRatio] = useState(1.414);
  const [error, setError] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!visible || !width) {
      if (canvas.current) { canvas.current.width = 0; canvas.current.height = 0; }
      setReady(false);
      return;
    }
    let cancelled = false, task;
    setReady(false); setError(false);
    (async () => {
      const page = await pdf.getPage(number);
      if (cancelled) return;
      const original = page.getViewport({ scale: 1 });
      setRatio(original.height / original.width);
      const displayWidth = preview ? Math.min(width, 360 / (original.height / original.width)) : width;
      const viewport = page.getViewport({ scale: displayWidth / original.width });
      const density = Math.max(1, Math.min(window.devicePixelRatio || 1, 2, 4096 / viewport.width,
        Math.sqrt(12000000 / (viewport.width * viewport.height))));
      const element = canvas.current;
      element.width = Math.ceil(viewport.width * density);
      element.height = Math.ceil(viewport.height * density);
      element.style.width = viewport.width + "px";
      element.style.height = viewport.height + "px";
      task = page.render({ canvasContext: element.getContext("2d"), viewport,
        transform: density === 1 ? null : [density, 0, 0, density, 0, 0], background: "white" });
      await task.promise;
      if (!cancelled) setReady(true);
    })().catch((cause) => {
      if (!cancelled && cause.name !== "RenderingCancelledException") setError(true);
    });
    return () => { cancelled = true; task?.cancel(); };
  }, [pdf, number, width, preview, visible]);
  return <div ref={container} className={"pdf-page" + (preview ? " pdf-page-preview" : "")}
    data-page={number} style={preview ? undefined : { minHeight: width * ratio }}>
    {visible && !ready && <span className="pdf-page-status" role="status">{error ? "Não foi possível exibir esta página." : "Carregando página " + number + "…"}</span>}
    <canvas ref={canvas} aria-label={"Página " + number} role="img" style={{ visibility: ready ? "visible" : "hidden" }} />
  </div>;
}

function PdfReader({ pdf, title, downloadControl, onClose }) {
  const dialog = useRef(null), pages = useRef(null), closeButton = useRef(null);
  const titleId = useId();
  const width = useWidth(pages);
  const [zoom, setZoom] = useState(1), [pageNumber, setPageNumber] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    const keyboard = (event) => {
      if (event.key === "Escape") { event.preventDefault(); onClose(); }
      if (event.key === "Tab") {
        const controls = [...dialog.current.querySelectorAll('button:not(:disabled), input, a[href], [tabindex="0"]')];
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    const changed = () => setFullscreen(document.fullscreenElement === dialog.current);
    document.addEventListener("keydown", keyboard);
    document.addEventListener("fullscreenchange", changed);
    return () => {
      document.removeEventListener("keydown", keyboard);
      document.removeEventListener("fullscreenchange", changed);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose]);
  const close = () => {
    if (document.fullscreenElement === dialog.current) document.exitFullscreen?.().catch(() => {});
    onClose();
  };
  const goToPage = (value) => {
    const next = Math.max(1, Math.min(pdf.numPages, Number(value) || 1));
    setPageNumber(next);
    pages.current?.querySelector('[data-page="' + next + '"]')?.scrollIntoView({ block: "start" });
  };
  return createPortal(<div className="pdf-reader" ref={dialog} role="dialog" aria-modal="true" aria-labelledby={titleId}>
    <header className="pdf-reader-header">
      <h2 id={titleId}>{title}</h2>
      <div className="pdf-reader-actions">{downloadControl}<button ref={closeButton} onClick={close} aria-label="Fechar PDF">Fechar ×</button></div>
    </header>
    <div className="pdf-reader-toolbar" aria-label="Controles do PDF">
      <label>Página <input aria-label="Ir para página" type="number" min={1} max={pdf.numPages} value={pageNumber} onChange={(event) => goToPage(event.target.value)} /></label>
      <span>de {pdf.numPages}</span>
      <button onClick={() => setZoom((value) => Math.max(0.5, value - 0.25))} disabled={zoom <= 0.5} aria-label="Diminuir zoom">−</button>
      <span className="pdf-zoom">{Math.round(zoom * 100)}%</span>
      <button onClick={() => setZoom((value) => Math.min(3, value + 0.25))} disabled={zoom >= 3} aria-label="Aumentar zoom">+</button>
      <button onClick={() => setZoom(1)}>Ajustar à largura</button>
      {document.fullscreenEnabled && <button onClick={() => {
        const action = fullscreen ? document.exitFullscreen() : dialog.current.requestFullscreen();
        action?.catch(() => {});
      }}>{fullscreen ? "Sair da tela cheia" : "Tela cheia"}</button>}
    </div>
    <div ref={pages} className="pdf-reader-pages" tabIndex={0} aria-label="Todas as páginas do PDF">
      <div className="pdf-reader-paper" style={{ width: Math.max(1, Math.min(width - 24, 1100)) * zoom }}>
        {Array.from({ length: pdf.numPages }, (_, index) => <PdfPage key={index} pdf={pdf} number={index + 1}
          width={Math.max(1, Math.min(width - 24, 1100)) * zoom} scrollRoot={pages.current} />)}
      </div>
    </div>
  </div>, document.body);
}

function PdfAttachment({ fileUrl, fileName, downloadControl }) {
  const container = useRef(null);
  const visible = useVisible(container), width = useWidth(container);
  const [pdf, setPdf] = useState(null), [error, setError] = useState(""), [retry, setRetry] = useState(0);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!visible) return;
    let cancelled = false, loadingTask;
    const abort = new AbortController();
    setPdf(null); setError("");
    (async () => {
      const [library, bytes] = await Promise.all([loadPdfLibrary(), fetchPdfBytes(fileUrl, fileName, abort.signal)]);
      if (cancelled) return;
      const assets = new URL(PDFJS_PATH, window.location.origin).href;
      loadingTask = library.getDocument({ data: bytes, cMapUrl: assets + "cmaps/", cMapPacked: true,
        standardFontDataUrl: assets + "standard_fonts/", wasmUrl: assets + "wasm/" });
      const loaded = await loadingTask.promise;
      if (!cancelled) setPdf(loaded);
    })().catch((cause) => {
      if (!cancelled) setError(cause.name === "PasswordException"
        ? "Este PDF está protegido por senha. Use o download para abri-lo no seu leitor."
        : "Não foi possível carregar a prévia do PDF.");
    });
    return () => { cancelled = true; abort.abort(); loadingTask?.destroy().catch(() => {}); };
  }, [fileUrl, fileName, visible, retry]);
  const title = fileName || "Arquivo PDF";
  return <section ref={container} className="pdf-attachment" aria-label={title}>
    <div className="pdf-attachment-header"><strong>{title}</strong><div className="pdf-attachment-actions">
      <button disabled={!pdf} onClick={() => setExpanded(true)}>Abrir PDF</button>{downloadControl}
    </div></div>
    {error ? <div className="pdf-preview-message" role="alert">{error}<button onClick={() => setRetry((value) => value + 1)}>Tentar novamente</button></div> :
      <button className="pdf-preview" disabled={!pdf} onClick={() => setExpanded(true)} aria-label={"Abrir " + title + " para leitura completa"}>
        {pdf ? <PdfPage pdf={pdf} number={1} width={Math.max(1, width - 24)} preview /> : <span className="pdf-preview-message" role="status">Carregando PDF…</span>}
      </button>}
    {pdf && <div className="pdf-attachment-footer">{pdf.numPages} {pdf.numPages === 1 ? "página" : "páginas"} · Clique na prévia para ler o PDF completo</div>}
    {expanded && pdf && <PdfReader pdf={pdf} title={title} downloadControl={downloadControl} onClose={() => setExpanded(false)} />}
  </section>;
}
export default function PdfViewer(props) {
  // Trocar aula/arquivo desmonta o leitor anterior e cancela a solicitação pendente.
  return <PdfAttachment key={props.fileUrl} {...props} />;
}
